import { Link } from "react-router-dom";
import {
  Search,
  Library,
  ShieldCheck,
  Leaf,
  FlaskConical,
  AlertTriangle,
  ArrowRight,
  Sparkles,
  Info,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SubscribeForm } from "@/components/SubscribeForm";
import { Footer } from "@/components/Footer";
import { useLang } from "@/i18n";
import { usePageTitle } from "@/lib/useTitle";
import { Button } from "@/components/ui/button";

export default function Home() {
  const { t } = useLang();
  usePageTitle(t("title.home"));

  const steps = [
    { icon: FlaskConical, title: t("how.s1.title"), text: t("how.s1.text") },
    { icon: Leaf, title: t("how.s2.title"), text: t("how.s2.text") },
    { icon: ShieldCheck, title: t("how.s3.title"), text: t("how.s3.text") },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* Notice banner */}
      <div data-testid="dev-notice-banner" className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex gap-3 items-start">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" strokeWidth={2} />
          <p className="text-sm leading-relaxed text-amber-900">{t("banner.dev")}</p>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sage/10 text-sage-dark font-data text-xs uppercase tracking-[0.15em] mb-6 font-semibold">
              <ShieldCheck className="h-3.5 w-3.5" /> {t("hero.badge")}
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-ink leading-[0.98]">
              {t("hero.title1")}
              <br />
              <span className="text-sage-dark">{t("hero.title2")}</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-gray-700 leading-relaxed max-w-xl">
              {t("hero.subtitle")}
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/archiv" data-testid="cta-search-batch">
                <Button className="h-14 px-7 w-full sm:w-auto rounded-sm bg-ink text-white hover:bg-sage-dark font-semibold text-base gap-2">
                  <Search className="h-5 w-5" strokeWidth={2} />
                  {t("cta.search")}
                </Button>
              </Link>
              <Link to="/archiv?tab=suroviny" data-testid="cta-browse-history">
                <Button variant="outline" className="h-14 px-7 w-full sm:w-auto rounded-sm border-ink/20 text-ink hover:bg-secondary font-semibold text-base gap-2">
                  <Library className="h-5 w-5" strokeWidth={2} />
                  {t("cta.browse")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <span className="font-data text-xs uppercase tracking-[0.2em] text-gray-500 font-semibold">
          {t("how.kicker")}
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl text-ink mt-2 mb-12">{t("how.title")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((s, i) => (
            <div key={i} className="p-7 border border-border rounded-sm bg-white animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-sage/10">
                <s.icon className="h-5 w-5 text-sage-dark" strokeWidth={2} />
              </span>
              <h3 className="font-heading text-xl text-ink mt-5">{s.title}</h3>
              <p className="text-gray-700 text-sm leading-relaxed mt-2">{s.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <Link to="/archiv" data-testid="explore-archive-link">
            <Button variant="ghost" className="rounded-sm gap-2 text-sage-dark hover:text-ink text-base font-semibold">
              {t("how.archiveLink")} <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Subscriptions */}
      <section className="border-t border-border bg-secondary/40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <span className="font-data text-xs uppercase tracking-[0.2em] text-gray-500 font-semibold">
            {t("subs.kicker")}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl text-ink mt-2 mb-10">{t("subs.title")}</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SubscribeForm
              testid="crisis-subscribe"
              accent="amber"
              icon={AlertTriangle}
              endpoint="/subscribe/crisis"
              title={t("crisis.title")}
              description={t("crisis.desc")}
            />
            <SubscribeForm
              testid="marketing-subscribe"
              accent="sage"
              icon={Sparkles}
              endpoint="/subscribe/marketing"
              title={t("marketing.title")}
              description={t("marketing.desc")}
            />
          </div>

          <div data-testid="dev-tech-notice" className="mt-8 flex gap-3 items-start p-5 rounded-sm border border-border bg-white">
            <Info className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" strokeWidth={2} />
            <p className="text-sm text-gray-700 leading-relaxed">
              <span className="font-bold text-ink">{t("tech.notice.label")}</span> {t("tech.notice.text")}
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
